// Customizable Area Start
import React, { useState, useEffect } from "react";
import { createStyles, Theme } from "@material-ui/core/styles";
import { Box, Typography, Button, withStyles } from "@material-ui/core";
import { PostTypes, ChoiceLetters } from "./enums";
import { Choice, NormalizedFile } from "./types";
import { everyBrandLogoFilled } from "./assets";
import { CircularProgress } from "@material-ui/core";


type Props = {
  classes: any;
  closeModal: () => void;
  postPreview: string;
  selectedFile: NormalizedFile;
  videoPreviewRef: React.RefObject<HTMLVideoElement>;
  postType: PostTypes;
  choices: Choice[];
  postSocialScrollText: string;
  postEveryreelText: string;
  runPostFunction: () => void;
  isLoading: boolean;
};

const styles = (theme: Theme) =>
  createStyles({
    postPreview: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      background: "#ffffff",
      borderRadius: "20px",
      maxWidth: "400px",
      margin: "0 auto",
    },

    previewTitle: {
      fontSize: "18px",
      fontWeight: 700,
      lineHeight: "24px",
      letterSpacing: "-0.12px",
      color: "#000",
      textAlign: "center",
      marginBottom: "10px",
    },

    imagePreview: {
      position: "relative",
      display: "flex",
      width: "270px", // Instagram Reels width
      height: "480px", // Instagram Reels height (9:16 aspect ratio)
      borderRadius: "12px", // Instagram Reels border radius
      overflow: "hidden",
      background: "#000",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
      border: "1px solid #333",

      "& > img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },

      "& > video": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },
    },

    choicesPreview: {
      position: "absolute",
      bottom: "20px",
      left: 0,
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      width: "100%",
      padding: "0 15px",
    },

    captionPreview: {
      marginBottom: "8px",
      color: "#FFF",
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "20px",
      textShadow: "0 1px 3px rgba(0, 0, 0, 0.8)",
      maxWidth: "240px",
    },

    socialScrollPreviewOverlay: {
      position: "absolute",
      bottom: "20px",
      display: "flex",
      flexDirection: 'column',
      alignItems: "center",
      width: "100%",
      padding: "0 15px",
    },

    socialScrollCaption: {
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      textOverflow: "ellipsis",
      width: "90%",
      height: "auto",
      marginBottom: "20px",
      color: "#FFF",
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "20px",
      textShadow: "0 1px 3px rgba(0, 0, 0, 0.8)",
      textAlign: "center",
    },

    everybrandLogoContainer: {
      display: "flex",
      width: "100%",
      justifyContent: "center",
      alignItems: "center",
      gap: 0,
      fontSize: "8px",
      fontWeight: 700,
      color: "#FFF",
    },

    everybrandText: {
      paddingTop: "3px",
      fontSize: "8px",
      fontWeight: 700,
      opacity: "0.5"
    },

    linkPreview: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      width: "100%",
      maxWidth: "240px",
      height: "36px",
      borderRadius: "18px",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      color: "#000",
      fontSize: "14px",
      fontWeight: 700,
      fontFamily: "Inter, 'sans-serif'",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
    },

    linksContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },

    choicePreviewItem: {
      display: "flex",
      padding: "6px 8px",
      alignItems: "center",
      gap: "10px",
      height: "36px",
      borderRadius: "18px",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
    },

    choiceName: {
      color: "#FFF",
      fontSize: "13px",
      lineHeight: "16px", 
      fontWeight: 600,
      letterSpacing: "-0.1px",
      maxWidth: "180px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },

    choiceLetter: {
      flexShrink: 0,
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      backgroundColor: "#FFC629",
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "24px",  
      textAlign: "center",
      color: "#000",
    },

    postButton: {
      width: "100%",
      maxWidth: "270px",
      textTransform: "none",
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "24px",
      height: "48px",
      borderRadius: "24px",
      backgroundColor: "#FFC629",
      color: "#000",
      boxShadow: "0 4px 12px rgba(255, 198, 41, 0.3)",
      "&:hover": {
        backgroundColor: "#eebe40",
        boxShadow: "0 6px 16px rgba(255, 198, 41, 0.4)",
      },
      "&:disabled": {
        backgroundColor: "#FFC629",
        color: "#000",
        opacity: 0.7,
        cursor: "not-allowed",
      }
    },
  });

export const CreateEveryreelSettingsPostPreview = ({
  classes,
  postPreview,
  selectedFile,
  videoPreviewRef,
  postType,
  choices,
  postSocialScrollText,
  postEveryreelText,
  runPostFunction,
  isLoading
}: Props) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    const video = videoPreviewRef.current;
    if (!video) return;

    const handlePlay = () => setIsVideoPlaying(true);
    const handlePause = () => setIsVideoPlaying(false);
    const handleEnded = () => setIsVideoPlaying(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [videoPreviewRef]);

  useEffect(() => {
    const video = videoPreviewRef.current;
    if (!video || selectedFile.type.includes("image")) return;

    // Changing the child <source> src does not reload a <video> element on its own
    video.load();
    setIsVideoPlaying(false);
  }, [selectedFile.meta.preview, selectedFile.type, videoPreviewRef]);

  return (
    <Box className={classes.postPreview}>
      <Typography className={classes.previewTitle}>{postPreview}</Typography>
      <Box className={classes.imagePreview}>
        {selectedFile.type.includes("image") ? (
          <img src={selectedFile.meta.preview} alt="image preview" />
        ) : (
          <video 
            controls={false} 
            ref={videoPreviewRef} 
            muted
            onClick={() => {
              if (videoPreviewRef.current) {
                if (videoPreviewRef.current.paused) {
                  videoPreviewRef.current.play();
                } else {
                  videoPreviewRef.current.pause();
                }
              }
            }}
            style={{ cursor: "pointer",objectFit: "contain", }}
          >
            <source src={selectedFile.meta.preview} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
        {/* Instagram Reels style play button overlay for videos - only show when paused */}
        {!selectedFile.type.includes("image") && !isVideoPlaying && (
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              transition: "opacity 0.3s ease",
            }}
            onClick={() => {
              if (videoPreviewRef.current) {
                if (videoPreviewRef.current.paused) {
                  videoPreviewRef.current.play();
                } else {
                  videoPreviewRef.current.pause();
                }
              }
            }}
          >
            <Box
              style={{
                width: 0,
                height: 0,
                borderLeft: "20px solid #000",
                borderTop: "12px solid transparent",
                borderBottom: "12px solid transparent",
                marginLeft: "4px",
              }}
            />
          </Box>
        )}

        {postType === PostTypes.INTERACTIVE_FUNNEL && (
          <Box className={classes.choicesPreview}>
            <Typography className={classes.captionPreview}>
              {selectedFile?.caption}
            </Typography>
            <Box className={classes.linksContainer}>
              {choices.map((choice, i) => (
                <Box key={String(i)} className={classes.choicePreviewItem}>
                  <Box className={classes.choiceLetter}>{ChoiceLetters[i]}</Box>
                  <Typography className={classes.choiceName}>
                    {choice.name}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box className={classes.everybrandLogoContainer}>
              <Typography className={classes.everybrandText}>Powered by</Typography>
              <img
                src={everyBrandLogoFilled}
                className={classes.everybrandLogo}
                alt="everybrand"
              />
            </Box>
          </Box>
        )}

        {postType === PostTypes.SOCIAL_SCROLL && (
          <Box className={classes.socialScrollPreviewOverlay}>
            <Typography className={classes.socialScrollCaption}>
              {selectedFile?.caption}
            </Typography>

            {selectedFile.isLinkAllowed && (
              <Box className={classes.linkPreview}>{selectedFile.linkText}</Box>
            )}

            <Box className={classes.everybrandLogoContainer}>
              <Typography className={classes.everybrandText}>Powered by</Typography>
              <img
                src={everyBrandLogoFilled}
                className={classes.everybrandLogo}
                alt="everybrand"
              />
            </Box>
          

          
          </Box>
        )}
      </Box>
      <Button 
        id="postEveryreelButton" 
        className={classes.postButton} 
        onClick={runPostFunction}
        disabled={isLoading}
      >
        {isLoading ? (
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CircularProgress size={20} style={{ color: "#000" }} />
            <Typography style={{ color: "#000", fontSize: "16px", fontWeight: 700 }}>
              Posting...
            </Typography>
          </Box>
        ) : (
          postType === PostTypes.SOCIAL_SCROLL
            ? (postSocialScrollText || (
                <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CircularProgress size={16} style={{ color: "#000" }} />
                </Box>
              ))
            : (postEveryreelText || (
                <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CircularProgress size={16} style={{ color: "#000" }} />
                </Box>
              ))
        )}
      </Button>
      {isLoading && (
        <Box
          style={{
             position: "fixed",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999999999,
          }}
        >
          <CircularProgress size={50} style={{ color: "#FFC629" }} />
        </Box>
      )}
    </Box>
  );
};

export default withStyles(styles)(CreateEveryreelSettingsPostPreview);
// Customizable Area End
